import { useEffect, useMemo, useState } from 'react';
import ConflictDetailCard from './conflictDetailCard';
import { ConflictSections } from '../../constants/conflictSearch';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useFetchConflictSearchesById } from '../../hooks/useFetchConflictSearchesById';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import MattersListing from './mattersListing';
import MatterRequestListing from './matterRequestListing';
import ConflictCheckListing from './conflictCheckListing';
import Notes from './notes';
import { NotesEntity } from '../../constants/notes';
import { ICustomerType } from '../../constants/customer';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';

import CustomTabs from '../customTabs/customTabs';
import DetailScrollArea from '../detailScrollArea/detailScrollArea';

const ConflictDetailTab = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { setTitle } = usePage();
  const { goBack } = useNavigationStack();
  const { trackPage } = useAnalytics();
  const [selectedTab, setSelectedTab] = useState<string>(ConflictSections.general.toString());
  const { data: conflictSearchDetails, isLoading } = useFetchConflictSearchesById({
    id,
    relations: ['primaryAddress', 'responsibleUser'],
    fields: [
      'name',
      'homeCountry',
      'primaryPhone',
      'type',
      'personalId',
      'vatNumber',
      'email',
      'businessId',
      'hasMatterRequests',
      'isBlackListed',
      'hasActiveOpportunities',
      'hasActiveConflictChecks',
      'hasCoPartyRole',
      'hasOppositionRole',
      'hasOtherRole',
    ],
  });
  useEffect(() => {
    trackPage(AnalyticsPage.CONFLICT_SEARCH_DETAIL);
  }, [trackPage]);
  const handleBack = () => {
    goBack();
  };
  useEffect(() => {
    if (conflictSearchDetails && !isLoading) {
      setTitle(
        <AddEditTransactionHeader
          handleBack={handleBack}
          title={conflictSearchDetails?.name}
          subTitle={t('conflict:conflictSearch')}
        />,
      );
    }
  }, [conflictSearchDetails, isLoading]);

  const items = useMemo(
    () => [
      {
        key: ConflictSections.general.toString(),
        label: t('common:tab.general'),
        children: (
          <DetailScrollArea>
            <ConflictDetailCard details={conflictSearchDetails} isLoading={isLoading} />
          </DetailScrollArea>
        ),
      },
      {
        key: ConflictSections.matters.toString(),
        label: t('common:tab.matters'),
        children: <MattersListing type={conflictSearchDetails?.type ?? ''} />,
      },
      {
        key: ConflictSections.matterRequests.toString(),
        label: t('common:tab.matterRequests'),
        children: <MatterRequestListing type={conflictSearchDetails?.type ?? ''} />,
      },
      {
        key: ConflictSections.conflictChecks.toString(),
        label: t('common:tab.conflictChecks'),
        children: <ConflictCheckListing type={conflictSearchDetails?.type ?? ''} />,
      },
      {
        key: ConflictSections.notes.toString(),
        label: t('common:tab.notes'),
        children: (
          <Notes
            entitySubject={conflictSearchDetails?.name ?? ''}
            entity={
              conflictSearchDetails?.type === ICustomerType.COMPANY
                ? NotesEntity.COMPANY
                : NotesEntity.PRIVATEPERSON
            }
          />
        ),
      },
    ],
    [t, conflictSearchDetails, isLoading],
  );
  return (
    <CustomTabs variant="pill" activeKey={selectedTab} onChange={setSelectedTab} items={items} />
  );
};

export default ConflictDetailTab;
